import { z } from 'zod';
import { EMBEDDED_LIMITS, SERVICE_TYPES } from '../../constants/index.js';
import { defaulted, mediaRefSchema, objectIdSchema, priceSchema, requiredTranslatable, seoMetaSchema, slugSchema, translatableString, } from '../common.js';
import { blocksArraySchema } from '../blocks/index.js';
import { auditableSchema, editableSchema, legacySchema, orderableSchema, publishableSchema, searchableSchema, } from './base.js';
// ─── Sahifalar ───────────────────────────────────────────────────────────────
/** O'chirib bo'lmaydigan tizim sahifalari (TZ 3.3). */
export const SYSTEM_PAGE_KEYS = ['home', 'about', 'contact', 'shop', 'droneshow'];
export const pageSchema = auditableSchema
    .merge(legacySchema)
    .merge(publishableSchema)
    .merge(orderableSchema)
    .merge(editableSchema)
    .merge(searchableSchema)
    .extend({
    /** Bosh sahifa uchun bo'sh satr */
    slug: z.union([z.literal(''), slugSchema]),
    title: requiredTranslatable({ max: 300 }),
    isSystem: z.boolean().default(false),
    systemKey: z.enum(SYSTEM_PAGE_KEYS).nullable().default(null),
    blocks: blocksArraySchema.default([]),
    parentId: objectIdSchema.nullable().default(null),
    seo: defaulted(seoMetaSchema),
});
// ─── Yangiliklar ─────────────────────────────────────────────────────────────
export const newsCategorySchema = auditableSchema
    .merge(orderableSchema)
    .merge(editableSchema)
    .extend({
    slug: slugSchema,
    name: requiredTranslatable({ max: 200 }),
});
export const newsSchema = auditableSchema
    .merge(legacySchema)
    .merge(publishableSchema)
    .merge(editableSchema)
    .merge(searchableSchema)
    .extend({
    slug: slugSchema,
    title: requiredTranslatable({ max: 300 }),
    excerpt: translatableString({ max: 1000 }),
    /** Sanitize qilingan HTML */
    body: translatableString(),
    coverImage: mediaRefSchema.nullable().default(null),
    gallery: z.array(mediaRefSchema).max(EMBEDDED_LIMITS.GALLERY_IMAGES).default([]),
    /**
     * YouTube havolasi — yangilik sahifasida video bo'lib ochiladi.
     *
     * Faqat MANZIL saqlanadi, video emas: fayl bizning serverimizda
     * turmaydi (2 GB disk) va uzatish tezligi ham YouTube zimmasida.
     * Bo'sh bo'lsa sahifada hech narsa chizilmaydi.
     */
    youtubeUrl: z.string().trim().max(300).default(''),
    categoryId: objectIdSchema.nullable().default(null),
    tags: z.array(z.string().trim().max(60)).max(20).default([]),
    isFeatured: z.boolean().default(false),
    seo: defaulted(seoMetaSchema),
});
// ─── Rahbariyat ──────────────────────────────────────────────────────────────
export const staffSchema = auditableSchema
    .merge(legacySchema)
    .merge(orderableSchema)
    .merge(publishableSchema)
    .merge(editableSchema)
    .merge(searchableSchema)
    .extend({
    slug: slugSchema,
    fullName: requiredTranslatable({ max: 300 }),
    position: requiredTranslatable({ max: 300 }),
    photo: mediaRefSchema.nullable().default(null),
    /** Sanitize qilingan HTML — ma'lumotnoma */
    biography: translatableString(),
    /**
     * «Ma'lumotnoma» xronologiyasi — maketda yil va matn qatorlari
     * (`C7 Rasmiy bo'limlar`, RAHBAR SAHIFASI). Bo'sh bo'lsa sahifa
     * `biography` HTML matnini ko'rsatadi.
     */
    biographyTimeline: z
        .array(z.object({
        year: z.string().trim().max(20),
        text: translatableString({ max: 1000 }),
    }))
        .max(40)
        .default([]),
    phone: z.string().trim().max(30).nullable().default(null),
    email: z.string().trim().max(200).nullable().default(null),
    receptionHours: translatableString({ max: 400 }),
});
// ─── Hujjatlar ───────────────────────────────────────────────────────────────
export const documentCategorySchema = auditableSchema
    .merge(orderableSchema)
    .merge(editableSchema)
    .extend({
    slug: slugSchema,
    name: requiredTranslatable({ max: 200 }),
});
export const documentSchema = auditableSchema
    .merge(orderableSchema)
    .merge(publishableSchema)
    .merge(editableSchema)
    .extend({
    title: requiredTranslatable({ max: 400 }),
    description: translatableString({ max: 2000 }),
    file: mediaRefSchema,
    documentNumber: z.string().trim().max(60).nullable().default(null),
    documentDate: z.coerce.date().nullable().default(null),
    categoryId: objectIdSchema.nullable().default(null),
    downloadCount: z.number().int().min(0).default(0),
});
// ─── Xizmatlar ───────────────────────────────────────────────────────────────
export const serviceTypeSchema = z.enum(SERVICE_TYPES);
/** Ta'mirlash narxlari jadvali (TZ 3.7). `price: null` → «Narx individual», `0` → «Bepul». */
export const servicePriceRowSchema = z.object({
    name: requiredTranslatable({ max: 300 }),
    price: priceSchema,
    duration: translatableString({ max: 200 }),
    order: z.number().int().min(0).default(0),
});
export const serviceSchema = auditableSchema
    .merge(legacySchema)
    .merge(orderableSchema)
    .merge(publishableSchema)
    .merge(editableSchema)
    .merge(searchableSchema)
    .extend({
    slug: slugSchema,
    serviceType: serviceTypeSchema.default('main'),
    title: requiredTranslatable({ max: 300 }),
    shortDescription: translatableString({ max: 1000 }),
    icon: z.string().trim().max(40).nullable().default(null),
    coverImage: mediaRefSchema.nullable().default(null),
    /** Xizmat sahifasi ham bloklardan yig'iladi */
    blocks: blocksArraySchema.default([]),
    priceList: z.array(servicePriceRowSchema).max(40).default([]),
    isFeatured: z.boolean().default(false),
    seo: defaulted(seoMetaSchema),
});
// ─── Loyihalar ───────────────────────────────────────────────────────────────
export const projectSchema = auditableSchema
    .merge(legacySchema)
    .merge(orderableSchema)
    .merge(publishableSchema)
    .merge(editableSchema)
    .merge(searchableSchema)
    .extend({
    slug: slugSchema,
    title: requiredTranslatable({ max: 300 }),
    shortDescription: translatableString({ max: 1000 }),
    description: translatableString(),
    coverImage: mediaRefSchema.nullable().default(null),
    gallery: z.array(mediaRefSchema).max(EMBEDDED_LIMITS.GALLERY_IMAGES).default([]),
    client: translatableString({ max: 300 }),
    location: translatableString({ max: 300 }),
    year: z.number().int().min(1990).max(2100).nullable().default(null),
    serviceIds: z.array(objectIdSchema).max(10).default([]),
    seo: defaulted(seoMetaSchema),
});
// ─── O'quv kurslari ──────────────────────────────────────────────────────────
export const courseScheduleSchema = z.object({
    startDate: z.coerce.date(),
    seatsTotal: z.number().int().min(0).nullable().default(null),
    seatsLeft: z.number().int().min(0).nullable().default(null),
    status: z.enum(['open', 'full', 'closed']).default('open'),
});
export const courseSchema = auditableSchema
    .merge(orderableSchema)
    .merge(publishableSchema)
    .merge(editableSchema)
    .merge(searchableSchema)
    .extend({
    slug: slugSchema,
    title: requiredTranslatable({ max: 300 }),
    description: translatableString(),
    program: z
        .array(z.object({
        title: requiredTranslatable({ max: 300 }),
        content: translatableString({ max: 4000 }),
        hours: z.number().int().min(0).nullable().default(null),
        order: z.number().int().min(0).default(0),
    }))
        .max(40)
        .default([]),
    durationHours: z.number().int().min(0).nullable().default(null),
    price: priceSchema,
    level: z.enum(['beginner', 'advanced', 'professional']).default('beginner'),
    certificate: z.boolean().default(true),
    instructorIds: z.array(objectIdSchema).max(10).default([]),
    coverImage: mediaRefSchema.nullable().default(null),
    gallery: z.array(mediaRefSchema).max(EMBEDDED_LIMITS.GALLERY_IMAGES).default([]),
    schedule: z.array(courseScheduleSchema).max(40).default([]),
    seo: defaulted(seoMetaSchema),
});
// ─── Dron-shou ───────────────────────────────────────────────────────────────
export const droneShowPackageSchema = auditableSchema
    .merge(orderableSchema)
    .merge(publishableSchema)
    .merge(editableSchema)
    .extend({
    slug: slugSchema,
    title: requiredTranslatable({ max: 300 }),
    description: translatableString({ max: 4000 }),
    droneCount: z.number().int().min(0).default(0),
    durationMinutes: z.number().int().min(0).default(0),
    priceFrom: priceSchema,
    features: z
        .array(translatableString({ max: 300 }))
        .max(20)
        .default([]),
    image: mediaRefSchema.nullable().default(null),
    isFeatured: z.boolean().default(false),
});
export const droneShowProjectSchema = auditableSchema
    .merge(orderableSchema)
    .merge(publishableSchema)
    .merge(editableSchema)
    .merge(searchableSchema)
    .extend({
    slug: slugSchema,
    title: requiredTranslatable({ max: 300 }),
    description: translatableString({ max: 4000 }),
    eventDate: z.coerce.date().nullable().default(null),
    city: translatableString({ max: 200 }),
    client: translatableString({ max: 300 }),
    droneCount: z.number().int().min(0).default(0),
    coverImage: mediaRefSchema.nullable().default(null),
    gallery: z.array(mediaRefSchema).max(EMBEDDED_LIMITS.GALLERY_IMAGES).default([]),
    videoUrl: z.string().max(300).nullable().default(null),
    seo: defaulted(seoMetaSchema),
});
//# sourceMappingURL=content.js.map